/**
 * Brand Beacon — fetches shared content from parent brand stores via Storefront API.
 * Region adaptation applies to all incoming beacon data (size charts today; more later).
 */
(function () {
  const REGISTRY = {
    wrightsock: {
      domain: 'iuravi-en.myshopify.com',
      apiVersion: '2025-01',
      sourceRegion: 'nz',
      defaultHandle: 'wrightsock-adult',
      kidsHandle: 'wrightsock-kids',
      kidsTags: ['Kids Socks', 'size-chart:wrightsock-kids'],
      tagPrefix: 'size-chart:',
    },
  };

  const REGION_LABELS = {
    nz: { womenShoe: 'NZ/USA', menShoe: 'NZ/UK', kidsUk: 'UK/NZ' },
    au: { womenShoe: 'AU/USA', menShoe: 'AU/UK', kidsUk: 'UK/AU' },
  };

  const VENDOR_KEYS = {
    wrightsock: 'wrightsock',
    'wright sock': 'wrightsock',
  };

  /** stores.json store IDs → brand registry key */
  const SOURCE_STORES = {
    'wrightsock-nz': 'wrightsock',
  };

  /** Token lookup aliases per brand (store IDs, shorthand keys, myshopify domains). */
  const TOKEN_KEY_ALIASES = {
    wrightsock: ['wrightsock', 'wrightsock-nz', 'iuravi-en.myshopify.com'],
  };

  function normaliseVendor(vendor) {
    return (vendor || '').trim().toLowerCase();
  }

  function resolveBrandKey(vendor) {
    return VENDOR_KEYS[normaliseVendor(vendor)] || null;
  }

  function resolveHandle(brand, productTags) {
    const config = REGISTRY[brand];
    if (!config) return null;

    const tagPrefix = config.tagPrefix || 'size-chart:';
    for (const tag of productTags || []) {
      if (tag.indexOf(tagPrefix) === 0) {
        return tag.slice(tagPrefix.length);
      }
    }

    for (const kidsTag of config.kidsTags || []) {
      if (productTags.includes(kidsTag)) return config.kidsHandle;
    }

    return config.defaultHandle;
  }

  function getConfigData() {
    const el = document.getElementById('brand-beacon-config');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  function countryToRegion(countryCode) {
    const code = (countryCode || '').toUpperCase();
    if (code === 'AU') return 'au';
    if (code === 'NZ') return 'nz';
    return 'nz';
  }

  function parseTokenMap(raw) {
    const map = {};
    const unknown = [];

    for (const line of String(raw || '').split('\n')) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;

      const pipe = trimmed.indexOf('|');
      if (pipe === -1) {
        console.warn('Brand Beacon: skipped line (use store-id | token):', trimmed);
        continue;
      }

      const storeId = trimmed.slice(0, pipe).trim().toLowerCase();
      const token = trimmed.slice(pipe + 1).trim();

      if (!storeId || !token) {
        console.warn('Brand Beacon: skipped incomplete line:', trimmed);
        continue;
      }

      map[storeId] = token;

      const brand = SOURCE_STORES[storeId];
      if (!brand && !Object.values(SOURCE_STORES).includes(storeId)) {
        unknown.push(storeId);
      }
    }

    if (unknown.length) {
      console.warn(
        'Brand Beacon: unknown store-id(s) — check theme settings or add to SOURCE_STORES in brand-beacon.js:',
        unknown.join(', ')
      );
    }

    return map;
  }

  function getTokenMap() {
    const data = getConfigData();
    return parseTokenMap(data.beacon_storefront_tokens);
  }

  function getRegion(brand) {
    const data = getConfigData();
    const storeRegion = countryToRegion(data.beacon_shop_country);
    const sourceRegion = REGISTRY[brand]?.sourceRegion || 'nz';

    // Regional labels/formatting for this store. When source region differs from
    // this store (e.g. NZ source on an AU store), output is adapted automatically.
    if (storeRegion !== sourceRegion) {
      return storeRegion;
    }

    return sourceRegion;
  }

  function getRegionLabels(brand) {
    const region = getRegion(brand);
    return REGION_LABELS[region] || REGION_LABELS.nz;
  }

  function getTokenForBrand(brand) {
    const map = getTokenMap();
    const config = REGISTRY[brand];
    if (!config) return '';

    const keys = new Set([brand]);
    for (const alias of TOKEN_KEY_ALIASES[brand] || []) keys.add(alias);
    if (config.domain) keys.add(config.domain.toLowerCase());
    for (const [storeId, mappedBrand] of Object.entries(SOURCE_STORES)) {
      if (mappedBrand === brand) keys.add(storeId);
    }

    for (const key of keys) {
      if (map[key]) return map[key];
    }

    return '';
  }

  async function fetchSizeChart(brand, handle) {
    const config = REGISTRY[brand];
    const token = getTokenForBrand(brand);
    if (!token) throw new Error('Beacon token not configured');

    const query = `
      query SizeChart($handle: MetaobjectHandleInput!) {
        metaobject(handle: $handle) {
          handle
          fields {
            key
            value
            references(first: 20) {
              nodes {
                ... on Metaobject {
                  fields { key value }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(
      `https://${config.domain}/api/${config.apiVersion}/graphql.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query,
          variables: { handle: { type: 'size_chart', handle } },
        }),
      }
    );

    if (!response.ok) throw new Error('Beacon request failed');
    const payload = await response.json();
    if (payload.errors?.length) throw new Error(payload.errors[0].message);
    return payload.data?.metaobject;
  }

  function fieldMap(metaobject) {
    const map = {};
    for (const field of metaobject.fields || []) {
      map[field.key] = field;
    }
    return map;
  }

  function renderWrightsockTable(metaobject, brand) {
    const labels = getRegionLabels(brand);
    const fields = fieldMap(metaobject);
    const layout = (fields.layout?.value || 'adult').toLowerCase();
    const isKids = layout === 'kids' || layout === 'kid';
    const title = fields.title?.value || 'Size chart';
    const rows = (fields.rows?.references?.nodes || []).map((row) => {
      const rowFields = {};
      for (const f of row.fields || []) rowFields[f.key] = f.value || '';
      return rowFields;
    });

    const colspan = isKids ? 4 : 6;
    let html = `<table class="size-chart"><thead class="size_chart-title"><tr><th colspan="${colspan}">${escapeHtml(title)}</th></tr></thead><tbody class="size_chart-sub_title">`;

    html += `<tr><th></th><th colspan="${isKids ? 3 : 5}" class="size_chart-grey_title">FIT SHOE SIZE</th></tr>`;

    if (isKids) {
      html += `<tr><th class="justify_text">Sock Size</th><th>US</th><th>${escapeHtml(labels.kidsUk)}</th><th>European</th></tr>`;
    } else {
      html +=
        '<tr><th></th><th colspan="2" class="size_chart-black_title">Women</th><th colspan="2" class="size_chart-black_title">Men</th><th></th></tr>';
      html += `<tr><th class="justify_text">Sock Size</th><th>${escapeHtml(labels.womenShoe)}</th><th>Euro</th><th>${escapeHtml(labels.menShoe)}</th><th>Euro</th><th>Foot Size</th></tr>`;
    }

    html += '</tbody><tbody class="size_chart-options">';

    for (const row of rows) {
      html += '<tr>';
      html += `<td>${escapeHtml(row.sock_size)}</td>`;
      if (isKids) {
        html += `<td>${escapeHtml(row.column_2)}</td><td>${escapeHtml(row.column_3)}</td><td>${escapeHtml(row.column_4)}</td>`;
      } else if (
        (row.sock_size || '').toLowerCase() === 'x-large' &&
        !row.column_2 &&
        !row.column_3
      ) {
        html += '<td colspan="2"></td>';
        html += `<td>${escapeHtml(row.column_4)}</td><td>${escapeHtml(row.column_5)}</td><td>${escapeHtml(row.column_6)}</td>`;
      } else {
        html += `<td>${escapeHtml(row.column_2)}</td><td>${escapeHtml(row.column_3)}</td><td>${escapeHtml(row.column_4)}</td><td>${escapeHtml(row.column_5)}</td><td>${escapeHtml(row.column_6)}</td>`;
      }
      html += '</tr>';
    }

    html += '</tbody></table>';
    return `<div class="size-chart-scroll">${html}</div>`;
  }

  function renderRetailerTable(metaobject, brand) {
    const labels = getRegionLabels(brand);
    const fields = fieldMap(metaobject);
    const layout = (fields.layout?.value || 'adult').toLowerCase();
    const isKids = layout === 'kids' || layout === 'kid';
    const title = fields.title?.value || 'Size chart';
    const rows = (fields.rows?.references?.nodes || []).map((row) => {
      const rowFields = {};
      for (const f of row.fields || []) rowFields[f.key] = f.value || '';
      return rowFields;
    });

    const colspan = isKids ? 4 : 6;
    let html = `<table class="brand-beacon-chart"><thead class="brand-beacon-chart__title"><tr><th colspan="${colspan}">${escapeHtml(title)}</th></tr></thead><tbody class="brand-beacon-chart__sub">`;

    html += `<tr><th></th><th colspan="${isKids ? 3 : 5}" class="brand-beacon-chart__grey">FIT SHOE SIZE</th></tr>`;

    if (isKids) {
      html += `<tr><th class="brand-beacon-chart__justify">Sock Size</th><th>US</th><th>${escapeHtml(labels.kidsUk)}</th><th>European</th></tr>`;
    } else {
      html +=
        '<tr><th></th><th colspan="2" class="brand-beacon-chart__black">Women</th><th colspan="2" class="brand-beacon-chart__black">Men</th><th></th></tr>';
      html += `<tr><th class="brand-beacon-chart__justify">Sock Size</th><th>${escapeHtml(labels.womenShoe)}</th><th>Euro</th><th>${escapeHtml(labels.menShoe)}</th><th>Euro</th><th>Foot Size</th></tr>`;
    }

    html += '</tbody><tbody class="brand-beacon-chart__rows">';

    for (const row of rows) {
      html += '<tr>';
      html += `<td>${escapeHtml(row.sock_size)}</td>`;
      if (isKids) {
        html += `<td>${escapeHtml(row.column_2)}</td><td>${escapeHtml(row.column_3)}</td><td>${escapeHtml(row.column_4)}</td>`;
      } else if (
        (row.sock_size || '').toLowerCase() === 'x-large' &&
        !row.column_2 &&
        !row.column_3
      ) {
        html += '<td colspan="2"></td>';
        html += `<td>${escapeHtml(row.column_4)}</td><td>${escapeHtml(row.column_5)}</td><td>${escapeHtml(row.column_6)}</td>`;
      } else {
        html += `<td>${escapeHtml(row.column_2)}</td><td>${escapeHtml(row.column_3)}</td><td>${escapeHtml(row.column_4)}</td><td>${escapeHtml(row.column_5)}</td><td>${escapeHtml(row.column_6)}</td>`;
      }
      html += '</tr>';
    }

    html += '</tbody></table>';
    return `<div class="brand-beacon-chart-scroll">${html}</div>`;
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function renderTable(metaobject, style, brand) {
    if (style === 'retailer') return renderRetailerTable(metaobject, brand);
    return renderWrightsockTable(metaobject, brand);
  }

  function editorErrorHtml() {
    const inEditor = window.Shopify && Shopify.designMode;
    return inEditor
      ? '<p class="brand-beacon-error">Size guide cannot load inside the theme editor. Save, then use <strong>Preview</strong> (opens the storefront) to test.</p>'
      : '<p class="brand-beacon-error">Size guide unavailable. Please try again later.</p>';
  }

  async function loadIntoElement(el, brand, handle, style) {
    el.innerHTML = '<p class="brand-beacon-loading">Loading size guide…</p>';
    try {
      const metaobject = await fetchSizeChart(brand, handle);
      if (!metaobject) throw new Error('Size chart not found');
      el.innerHTML = renderTable(metaobject, style, brand);
    } catch (err) {
      el.innerHTML = editorErrorHtml();
      console.warn('Brand Beacon:', err);
    }
  }

  function openModal(popup) {
    popup.hidden = false;
    popup.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  function closeModal(popup) {
    popup.hidden = true;
    popup.style.display = 'none';
    document.body.style.overflow = '';
  }

  function initRetailerRoot(root) {
    const trigger = root.querySelector('.brand-beacon-trigger');
    const popup = root.querySelector('.brand-beacon-popup');
    if (!trigger || !popup) return;

    if (popup.parentElement !== document.body) {
      document.body.appendChild(popup);
    }
    root.brandBeaconPopup = popup;

    let loaded = false;
    trigger.addEventListener('click', async () => {
      openModal(popup);
      if (!loaded) {
        loaded = true;
        const body = popup.querySelector('.brand-beacon-popup__body');
        await loadIntoElement(body, root.dataset.beaconBrand, root.dataset.beaconHandle, 'retailer');
      }
    });

    popup.querySelector('.brand-beacon-close')?.addEventListener('click', () => closeModal(popup));
    popup.addEventListener('click', (e) => {
      if (e.target === popup) closeModal(popup);
    });
  }

  function initLegacyPopup(popup) {
    const body = popup.querySelector('[data-brand-beacon-body]');
    if (!body) return;

    let loaded = false;

    async function loadIfNeeded() {
      if (loaded) return;
      loaded = true;
      await loadIntoElement(body, popup.dataset.beaconBrand, popup.dataset.beaconHandle, 'wrightsock');
    }

    window.openSizeChart = async function () {
      openModal(popup);
      await loadIfNeeded();
    };

    window.closeSizeChart = function () {
      closeModal(popup);
    };

    popup.addEventListener('click', (event) => {
      if (event.target === popup) closeModal(popup);
    });
  }

  function initPageBlocks() {
    document.querySelectorAll('[data-brand-beacon-page]').forEach((el) => {
      loadIntoElement(el, el.dataset.beaconBrand, el.dataset.beaconHandle, el.dataset.beaconStyle || 'wrightsock');
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-brand-beacon]').forEach(initRetailerRoot);
    const legacy = document.getElementById('sizeChartPopup');
    if (legacy && legacy.dataset.beaconBrand) initLegacyPopup(legacy);
    initPageBlocks();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.brand-beacon-popup:not([hidden]), #sizeChartPopup:not([hidden])').forEach((popup) => {
      closeModal(popup);
    });
  });

  window.BrandBeacon = {
    resolveBrandKey,
    resolveHandle,
    fetchSizeChart,
    renderTable,
    getRegion,
  };
})();
